import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { auth } from './firebaseConfig';
import ConflictResolutionModal from './ConflictResolutionModal';
import CustomerSelectionTable from './CustomerSelectionTable';
import { getCustomerListRef } from '../utils/databaseOperations';
import { deleteCustomerWithDebt } from './TransferCustomerModal';
import { toTurkishUpperCase } from '../utils/turkishUtils';

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  backgroundColor: '#ffffff',
  color: '#000000',
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  maxHeight: '80vh',
  overflowY: 'auto',
  border: '1px solid #ccc',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  width: '30px',
  height: '30px',
}));

const fetchAllCustomers = async (setError, setAlertOpen) => {
  const userEmail = auth.currentUser?.email;
  const db = getFirestore();
  
  if (!userEmail) {
    setError('Kullanıcı oturumu bulunamadı');
    setAlertOpen(true);
    return [];
  }

  try {
    const customerListRef = collection(db, `users/${userEmail}/customerList`);
    
    // Hem onay bekleyen hem de onaylanmış müşterileri getir
    const [pendingSnapshot, approvedSnapshot] = await Promise.all([
      getDocs(query(customerListRef, where('Onay', '==', 'Onay Bekliyor'))),
      getDocs(query(customerListRef, where('Onay', '==', 'Onaylandı')))
    ]);
    
    const pendingCustomers = pendingSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      status: 'Onay Bekliyor'
    }));
    
    const approvedCustomers = approvedSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      status: 'Onaylandı'
    }));
    
    // İki listeyi birleştir, önce onaylanmış müşteriler
    const allCustomers = [...approvedCustomers, ...pendingCustomers];
    
    console.log('Tüm müşteriler yüklendi:', {
      onaylanmış: approvedCustomers.length,
      bekleyenOnay: pendingCustomers.length,
      toplam: allCustomers.length
    });
    
    return allCustomers;
  } catch (error) {
    console.error('Müşteriler getirilemedi:', error);
    setError('Müşteriler getirilemedi.');
    setAlertOpen(true);
    return [];
  }
};

const AddCustomerModal = ({
  isOpen,
  onClose,
  setAlertOpen,
  setError,
  setSuccessMessage,
  initialCustomerName = '', // Puantajdan gelen müşteri adı için
  onCustomerAdded, // Müşteri eklendikten sonra çağrılacak callback
}) => {
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerCariCode, setNewCustomerCariCode] = useState('');
  const [newCustomerTaxNumber, setNewCustomerTaxNumber] = useState(''); // Vergi numarası
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [conflictingCustomer, setConflictingCustomer] = useState(null);
  const [isCustomerSelectionOpen, setIsCustomerSelectionOpen] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [tempCustomerData, setTempCustomerData] = useState(null);
  const [warningType, setWarningType] = useState(''); // 'missingInfo', 'conflict', 'other'
  const [warningMessage, setWarningMessage] = useState('');
  
  // Real-time validation için yeni state'ler - EditCustomerModal'den alındı
  const [nameConflict, setNameConflict] = useState(false);
  const [conflictingNameCustomer, setConflictingNameCustomer] = useState(null);
  const [codeConflict, setCodeConflict] = useState(false);
  const [conflictingCodeCustomer, setConflictingCodeCustomer] = useState(null);
  
  // Validation state'leri
  const [validationErrors, setValidationErrors] = useState({
    customerName: false,
    cariCode: false
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [conflictingSantiye, setConflictingSantiye] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const memoizedFetchAllCustomers = useCallback(fetchAllCustomers, [setError, setAlertOpen]);

  useEffect(() => {
    memoizedFetchAllCustomers().then(setAllCustomers);
  }, [memoizedFetchAllCustomers]);

  // Real-time müşteri adı kontrolü - EditCustomerModal'den alındı
  useEffect(() => {
    if (!newCustomerName.trim()) {
      setNameConflict('empty');
      setConflictingNameCustomer(null);
    } else {
      const conflictCustomer = allCustomers.find(c => c['Müşteri Adı']?.trim().toLowerCase() === newCustomerName.trim().toLowerCase());
      setNameConflict(conflictCustomer ? 'conflict' : false);
      setConflictingNameCustomer(conflictCustomer || null);
    }
  }, [newCustomerName, allCustomers]);

  // Real-time cari kod kontrolü - EditCustomerModal'den alındı
  useEffect(() => {
    if (!newCustomerCariCode.trim()) {
      setCodeConflict('empty');
      setConflictingCodeCustomer(null);
    } else {
      const conflictCustomer = allCustomers.find(c => (c.cariCode || '').trim().toLowerCase() === (newCustomerCariCode || '').trim().toLowerCase());
      setCodeConflict(conflictCustomer ? 'conflict' : false);
      setConflictingCodeCustomer(conflictCustomer || null);
    }
  }, [newCustomerCariCode, allCustomers]);

  // Puantajdan gelen müşteri adını otomatik doldur - Türkçe büyük harfe çevir
  useEffect(() => {
    if (isOpen && initialCustomerName) {
      setNewCustomerName(toTurkishUpperCase(initialCustomerName));
    }
  }, [isOpen, initialCustomerName]);

  const handleAddNewCustomer = async () => {
    // Detaylı validation kontrolü
    const errors = {
      customerName: !newCustomerName.trim(),
      cariCode: !newCustomerCariCode.trim()
    };
    
    setValidationErrors(errors);
    setShowValidationErrors(true);
    
    // Eğer hata varsa detaylı uyarı göster
    if (errors.customerName && errors.cariCode) {
      setWarningType('missingInfo');
      setWarningMessage('Müşteri adı ve cari kod zorunlu alanlar. Lütfen her ikisini de doldurun.');
      setIsWarningModalOpen(true);
      return;
    } else if (errors.customerName) {
      setWarningType('missingInfo');
      setWarningMessage('Müşteri adı zorunlu bir alan. Lütfen müşteri adını girin.');
      setIsWarningModalOpen(true);
      return;
    } else if (errors.cariCode) {
      setWarningType('missingInfo');
      setWarningMessage('Cari kod zorunlu bir alan. Lütfen cari kod girin.');
      setIsWarningModalOpen(true);
      return;
    }
    
    // Validation geçtiyse, hataları temizle ve işlemi devam ettir
    setShowValidationErrors(false);
    await checkConflictsAndAdd();
  };

  const checkConflictsAndAdd = async () => {
    const userEmail = auth.currentUser?.email;
    const db = getFirestore();
    
    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      console.log('🔍 Yeni müşteri çakışma kontrolü başlıyor:', {
        newCustomerName: newCustomerName.trim(),
        newCustomerCariCode: newCustomerCariCode.trim()
      });

      const customerListRef = collection(db, `users/${userEmail}/customerList`);
      
      // Müşteri adı çakışması kontrolü
      const nameConflictQuery = query(
        customerListRef,
        where('Müşteri Adı', '==', newCustomerName.trim())
      );
      
      // Cari kod çakışması kontrolü
      const cariCodeConflictQuery = query(
        customerListRef,
        where('cariCode', '==', newCustomerCariCode.trim())
      );

      const [nameConflictSnapshot, cariCodeConflictSnapshot] = await Promise.all([
        getDocs(nameConflictQuery),
        getDocs(cariCodeConflictQuery),
      ]);

      // Eğer çakışma varsa modal aç
      if (!nameConflictSnapshot.empty || !cariCodeConflictSnapshot.empty) {
        const conflictingDoc = !nameConflictSnapshot.empty
          ? nameConflictSnapshot.docs[0]
          : cariCodeConflictSnapshot.docs[0];

        console.log('🚨 Çakışma bulundu, çakışma çözümleme modalı açılıyor');
        setConflictingCustomer({ ...conflictingDoc.data(), id: conflictingDoc.id });
        setIsConflictModalOpen(true);
        return;
      }

      console.log('✅ Çakışma tespit edilmedi, müşteri ekleniyor');
      await addCustomerToFirestore();
    } catch (error) {
      console.error('Yeni müşteri eklenirken bir hata oluştu:', error);
      setError(`Yeni müşteri eklenirken bir hata oluştu: ${error.message}`);
      setAlertOpen(true);
    }
  };

  const handleConflictResolution = async () => {
    setIsConflictModalOpen(false);
    const db = getFirestore();

    try {
      if (!conflictingCustomer || !conflictingCustomer.id) {
        throw new Error('Çakışan müşteri bilgisi eksik veya geçersiz.');
      }

      // Önce debt subcollection'ı ile birlikte sil
      await deleteCustomerWithDebt(auth.currentUser?.email, conflictingCustomer.id);
      await addCustomerToFirestore();

      setSuccessMessage('Çakışma çözüldü ve yeni müşteri eklendi.');
      setAlertOpen(true);
    } catch (error) {
      console.error('Çakışma çözümlenirken hata oluştu:', error);
      setError(`Çakışma çözümlenirken hata oluştu: ${error.message}`);
      setAlertOpen(true);
    }
  };

  const addCustomerToFirestore = async () => {
    const db = getFirestore();
    try {
      await addDoc(collection(db, `users/${auth.currentUser?.email}/customerList`), {
        'Müşteri Adı': newCustomerName.trim(),
        Telefon: (newCustomerPhone || '').trim(),
        'E-posta': (newCustomerEmail || '').trim(),
        Onay: 'Onaylandı',
        cariCode: newCustomerCariCode.trim(),
        vergiNumarasi: (newCustomerTaxNumber || '').replace(/\D/g, '').trim(), // Vergi numarası (kurumsal müşteri eşleşmesi için)
        Şantiye: '',
        parentId: null,
        userEmail: auth.currentUser?.email,
        createdBy: auth.currentUser?.email,
        createdAt: new Date().toISOString()
      });

      // Setup progress kontrolü - En az bir müşteri var mı?
      const userDocRef = doc(db, 'users', auth.currentUser?.email);
      await updateDoc(userDocRef, {
        'setupProgress.customers': true // Müşteri eklendi, setup tamamlandı
      });

      console.log('✅ Müşteri eklendi, setup progress güncellendi');

      setSuccessMessage('Yeni müşteri başarıyla eklendi.');
      setAlertOpen(true);
      onClose();
      resetForm();
      
      // Müşteri listesini yenile
      if (onCustomerAdded) {
        onCustomerAdded();
      }
    } catch (error) {
      console.error('Yeni müşteri eklenirken bir hata oluştu:', error);
      throw new Error(`Yeni müşteri eklenirken bir hata oluştu: ${error.message}`);
    }
  };

  const resetForm = () => {
    setNewCustomerName('');
    setNewCustomerPhone('');
    setNewCustomerEmail('');
    setNewCustomerCariCode('');
    setNewCustomerTaxNumber(''); // Vergi numarasını sıfırla
    
    // Validation hatalarını da temizle
    setValidationErrors({
      customerName: false,
      cariCode: false
    });
    setShowValidationErrors(false);
    
    // Real-time validation state'lerini de temizle
    setNameConflict(false);
    setConflictingNameCustomer(null);
    setCodeConflict(false);
    setConflictingCodeCustomer(null);
  };

  const handleSelectCustomer = (customer) => {
    // Müşteri adını Türkçe büyük harfe çevir
    setNewCustomerName(toTurkishUpperCase(customer['Müşteri Adı']));
    setNewCustomerCariCode(customer.cariCode);
    setNewCustomerPhone(customer.Telefon || '');
    setNewCustomerEmail(customer['E-posta'] || '');
    setNewCustomerTaxNumber(customer.vergiNumarasi || ''); // Vergi numarasını da al
    setIsCustomerSelectionOpen(false);
  };

  const handleWarningModalClose = () => {
    setIsWarningModalOpen(false);
    setTempCustomerData(null);
    setWarningType('');
    setWarningMessage('');
  };

  const handleCariCodeSubmit = async () => {
    if (!newCustomerName.trim() || !newCustomerCariCode.trim()) {
      setError('Müşteri adı ve cari kodu gereklidir.');
      setAlertOpen(true);
      return;
    }

    setIsWarningModalOpen(false);
    await checkConflictsAndAdd();
  };

  return (
    <>
      <Modal open={isOpen} onClose={() => {
        // Modal kapatıldığında validation hatalarını temizle
        setValidationErrors({
          customerName: false,
          cariCode: false
        });
        setShowValidationErrors(false);
        
        // Real-time validation state'lerini de temizle
        setNameConflict(false);
        setConflictingNameCustomer(null);
        setCodeConflict(false);
        setConflictingCodeCustomer(null);
        
        onClose();
      }}>
        <ModalBox>
          <Typography variant="h6" gutterBottom>
            Yeni Müşteri Ekle
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary', mb: 2 }}>
            Kullanıcı: {auth.currentUser?.email}
          </Typography>
          <Box display="flex" alignItems="center">
            <TextField
              label="Müşteri Adı"
              fullWidth
              margin="normal"
              value={newCustomerName}
              onChange={(e) => {
                // Müşteri adını otomatik olarak Türkçe büyük harfe çevir
                setNewCustomerName(toTurkishUpperCase(e.target.value));
                // Kullanıcı yazmaya başladığında validation hatası varsa temizle
                if (showValidationErrors && validationErrors.customerName && e.target.value.trim()) {
                  setValidationErrors(prev => ({ ...prev, customerName: false }));
                }
              }}
              error={showValidationErrors && validationErrors.customerName}
              helperText={
                showValidationErrors && validationErrors.customerName 
                  ? "🚫 Müşteri adı zorunlu bir alandır. Lütfen doldurun." 
                  : "💡 Müşteri adı otomatik olarak büyük harfe çevrilir"
              }
              sx={{
                '& .MuiFormHelperText-root.Mui-error': {
                  color: '#d32f2f',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  backgroundColor: '#ffebee',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  marginTop: '8px'
                },
                '& .MuiFormHelperText-root:not(.Mui-error)': {
                  color: '#666',
                  fontSize: '0.75rem'
                }
              }}
            />
            {nameConflict === 'empty' && (
              <div style={{ color: 'red', fontSize: 13, marginLeft: 10 }}>
                Bu alan boş bırakılamaz!
              </div>
            )}
            {nameConflict === 'conflict' && conflictingNameCustomer && (
              <div style={{ color: 'red', fontSize: 13, marginLeft: 10 }}>
                Bu ad {conflictingNameCustomer['Müşteri Adı']} - {conflictingNameCustomer.cariCode} müşterisinde mevcut!
              </div>
            )}
            <StyledIconButton
              onClick={async () => {
                const newState = !isCustomerSelectionOpen;
                setIsCustomerSelectionOpen(newState);
                if (newState) {
                  const customers = await fetchAllCustomers(setError, setAlertOpen);
                  setAllCustomers(customers);
                }
              }}
              size="small"
            >
              <ArrowDropDownIcon fontSize="small" />
            </StyledIconButton>
          </Box>
          {isCustomerSelectionOpen && (
            <CustomerSelectionTable
              customers={allCustomers}
              onSelectCustomer={handleSelectCustomer}
              enableAutoUppercase={true}
            />
          )}
          <TextField
            label="Telefon"
            fullWidth
            margin="normal"
            value={newCustomerPhone}
            onChange={(e) => setNewCustomerPhone(e.target.value)}
          />
          <TextField
            label="E-posta"
            fullWidth
            margin="normal"
            value={newCustomerEmail}
            onChange={(e) => setNewCustomerEmail(e.target.value)}
          />
          <TextField
            label="Vergi Numarası (Kurumsal Müşteriler İçin)"
            fullWidth
            margin="normal"
            value={newCustomerTaxNumber}
            onChange={(e) => setNewCustomerTaxNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
            inputProps={{ maxLength: 11 }}
            helperText="10 veya 11 haneli vergi numarası (opsiyonel). Kurumsal müşterilerin puantaj görüntülemesi için gereklidir."
            sx={{
              '& .MuiFormHelperText-root': {
                color: '#666',
                fontSize: '0.75rem'
              }
            }}
          />
          <TextField
            label="Cari Kodu"
            fullWidth
            margin="normal"
            value={newCustomerCariCode}
            onChange={(e) => {
              setNewCustomerCariCode(e.target.value);
              // Kullanıcı yazmaya başladığında validation hatası varsa temizle
              if (showValidationErrors && validationErrors.cariCode && e.target.value.trim()) {
                setValidationErrors(prev => ({ ...prev, cariCode: false }));
              }
            }}
            error={showValidationErrors && validationErrors.cariCode}
            helperText={
              showValidationErrors && validationErrors.cariCode 
                ? "🚫 Cari kod zorunlu bir alandır. Lütfen doldurun." 
                : "📝 Örnek: A-001, MŞT-123, KT-45"
            }
            sx={{
              '& .MuiFormHelperText-root.Mui-error': {
                color: '#d32f2f',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                backgroundColor: '#ffebee',
                padding: '4px 8px',
                borderRadius: '4px',
                marginTop: '8px'
              },
              '& .MuiFormHelperText-root:not(.Mui-error)': {
                color: '#666',
                fontSize: '0.75rem'
              }
            }}
          />
          {codeConflict === 'empty' && (
            <div style={{ color: 'red', fontSize: 13, marginTop: 4 }}>
              Bu alan boş bırakılamaz!
            </div>
          )}
          {codeConflict === 'conflict' && conflictingCodeCustomer && (
            <div style={{ color: 'red', fontSize: 13, marginTop: 4 }}>
              Bu cari kodu {conflictingCodeCustomer['Müşteri Adı']} - {conflictingCodeCustomer.cariCode} müşterisinde mevcut!
            </div>
          )}
          {/* Formun en altına, ad veya cari kod çakışıyorsa genel bir uyarı ekle */}
          {(nameConflict === 'conflict' || codeConflict === 'conflict') && (
            <div style={{ color: 'red', fontWeight: 500, marginTop: 16, fontSize: 15 }}>
              Bu müşteri adı veya cari kodu başka bir müşteride mevcut. Dilerseniz bu müşterinin verisini aktarım yoluyla iletebilirsiniz.
            </div>
          )}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddNewCustomer}
            >
              Ekle
            </Button>
          </Box>
        </ModalBox>
      </Modal>

      <ConflictResolutionModal
        isOpen={isConflictModalOpen}
        onClose={() => setIsConflictModalOpen(false)}
        onConfirm={handleConflictResolution}
        conflictingCustomer={conflictingCustomer}
        newCustomerData={{
          'Müşteri Adı': newCustomerName,
          cariCode: newCustomerCariCode,
        }}
      />

      <Modal open={isWarningModalOpen} onClose={handleWarningModalClose}>
        <ModalBox>
          <Typography variant="h6" gutterBottom sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
            ⚠️ Eksik Bilgi Uyarısı
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mt: 2, mb: 2 }}>
            {warningMessage || "Lütfen eksik bilgileri tamamlayın."}
          </Typography>
          {warningType === 'missingInfo' && (
            <Box sx={{ 
              backgroundColor: '#fff3e0', 
              padding: 2, 
              borderRadius: 1, 
              border: '1px solid #ffb74d',
              mb: 2 
            }}>
              <Typography variant="body2" sx={{ color: '#e65100' }}>
                💡 <strong>İpucu:</strong> Tüm zorunlu alanları doldurduktan sonra tekrar deneyin.
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            {!newCustomerName.trim() && (
              <TextField
                label="Müşteri Adı"
                fullWidth
                margin="normal"
                value={newCustomerName}
                onChange={(e) => {
                  setNewCustomerName(toTurkishUpperCase(e.target.value));
                  // Validation hatası varsa temizle
                  if (validationErrors.customerName && e.target.value.trim()) {
                    setValidationErrors(prev => ({ ...prev, customerName: false }));
                  }
                }}
                error={validationErrors.customerName}
                helperText={validationErrors.customerName ? "Müşteri adı zorunludur" : ""}
                sx={{
                  '& .MuiFormHelperText-root.Mui-error': {
                    color: '#d32f2f',
                    fontWeight: 'bold'
                  }
                }}
              />
            )}
            {!newCustomerCariCode.trim() && (
              <TextField
                label="Cari Kodu"
                fullWidth
                margin="normal"
                value={newCustomerCariCode}
                onChange={(e) => {
                  setNewCustomerCariCode(e.target.value);
                  // Validation hatası varsa temizle
                  if (validationErrors.cariCode && e.target.value.trim()) {
                    setValidationErrors(prev => ({ ...prev, cariCode: false }));
                  }
                }}
                error={validationErrors.cariCode}
                helperText={validationErrors.cariCode ? "Cari kod zorunludur" : ""}
                sx={{
                  '& .MuiFormHelperText-root.Mui-error': {
                    color: '#d32f2f',
                    fontWeight: 'bold'
                  }
                }}
              />
            )}
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleWarningModalClose}
              sx={{ minWidth: '100px' }}
            >
              İptal
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCariCodeSubmit}
              sx={{ 
                minWidth: '100px',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              ✅ Kaydet
            </Button>
          </Box>
        </ModalBox>
      </Modal>
    </>
  );
};

export default AddCustomerModal;
